import React from 'react';
import './blog5MenstrualHealth.css';

const MenstrualHealthBlog = () => {
  return (
    <div className="menstrual-blog-container">
      <h1>Understanding Menstrual Health: When to Worry About Irregular Periods</h1>
      <p className="menstrual-subheading">A compassionate, clinic-guided journey into the rhythms of a woman’s body</p>

      <section>
        <h2 className="intro-blog5">🌸 Introduction: A Cycle That Speaks</h2>
        <p>
          Every woman’s body carries an internal rhythm — a cycle that whispers about her health, 
hormones, and well-being. Periods are not just about bleeding every month — they’re a vital 
sign. When they become irregular, unpredictable, heavy, or absent, the body is often 
sending a signal that something deeper needs attention. 
        </p>
        <p>
          At Sukam Speciality Clinic, we listen to those signals. Whether it’s a teenager’s first cycle 
that’s too far apart, a young adult with stubborn acne and delayed periods, or a woman in her 
30s suddenly facing heavy, painful menstruation — our goal is to help decode the message. 
        </p>
        <h2>This guide will help you understand:</h2>
        <ul>
          <li>What’s normal and what’s not in your cycle </li>
          <li>Common causes of menstrual irregularities </li>
          <li>Why early diagnosis matters </li>
          <li>How we evaluate and treat menstrual disorders gently and holistically</li>
        </ul>
      </section>

      <section>
        <h2> What Is Considered a “Normal” Period? </h2>
        <ul>
          <li>A cycle length of 21 to 35 days </li>
          <li>Bleeding lasts 2 to 7 days</li>
          <li>Around 25–80 ml blood loss (roughly 3–6 pads/day)</li>
          <li>Mild cramps, mood changes, and minor bloating </li>
        </ul>
        <p>
          But when these patterns shift — too frequent, too delayed, too heavy, or absent 
altogether — it’s time to take a closer look. 
        </p>
      </section>

      <section>
        <h2>⚠️ Common Types of Menstrual Irregularities</h2>
        <ul>
          <li><strong>Oligomenorrhea:</strong> Infrequent cycles (&gt;35 days apart)</li>
          <li><strong>Polymenorrhea:</strong> Frequent cycles (&lt;21 days apart)</li>
          <li><strong>Menorrhagia:</strong> Excessive/prolonged bleeding</li>
          <li><strong>Hypomenorrhea:</strong> Very light bleeding</li>
          <li><strong>Amenorrhea:</strong> No periods for over 3 months</li>
          <li><strong>Dysmenorrhea:</strong> Debilitating menstrual pain</li>
          <li><strong>Intermenstrual Bleeding:</strong> Spotting between periods</li>
        </ul>
        <p>
          Even if these seem manageable now, long-term neglect may lead to fertility issues, anemia, 
or underlying hormonal imbalances. 
        </p>
      </section>

      <section>
        <h2>🔍 Why Periods Become Irregular: Root Causes Explained </h2>
        <ul>
          <li><strong>PCOS:</strong> One of the most common reasons in young women. 
• Caused by hormonal imbalance (high androgens, insulin resistance). 
• Associated with: 
o Delayed or missed periods 
o Excess facial/body hair 
o Weight gain 
o Acne 
o Difficulty conceiving</li>
          <li><strong>Thyroid Disorders:</strong> Both hypothyroidism (low function) and hyperthyroidism (overactive) can disturb 
cycles. 
• Symptoms may include: 
o Fatigue, weight changes 
o Mood swings 
o Cold or heat intolerance 
o Irregular or absent periods </li>
          <li><strong>Stress and Mental Health: </strong> IChronic stress affects the hypothalamus, which regulates reproductive hormones. 
• Leads to delayed, missed, or erratic periods. 
• Common in students, working professionals, caregivers.</li>
          <li><strong> Sudden Weight Changes: </strong> Rapid weight loss, eating disorders, or extreme exercise (e.g., athletes) can stop periods. 
• Obesity can worsen PCOS and estrogen imbalance. </li>
          <li><strong>Fibroids and Uterine Polyps : </strong> Non-cancerous growths in the uterus. 
• Cause heavy, painful, or prolonged bleeding. </li>
          <li><strong>Pituitary Disorders :</strong> Prolactinoma and other pituitary tumors affect hormone release. 
• Can lead to missed periods, milky discharge from breasts. </li>
          <li><strong>Premature Ovarian Insufficiency (POI):</strong> Early depletion of ovarian function before age 40. 
• May present with irregular or stopped cycles and hot flashes.</li>
        </ul>
      </section>

      <section>
        <h2>🧪 How We Diagnose Menstrual Irregularities at Sukam </h2>
        <p>At Sukam, we don’t just ask about periods. We listen to your full story — your body, lifestyle, 
stress levels, nutrition, and family history. </p>
        <p>Investigations We Commonly Use: </p>
        <ul>
          <li>Pelvic ultrasound (to check ovaries, endometrium, fibroids)</li>
          <li>Hormonal profile: LH, FSH, TSH, Prolactin, AMH, Testosterone, Insulin</li>
          <li>CBC and Iron studies (for anemia) </li>
          <li>Thyroid function tests  </li>
          <li>Endometrial biopsy (in select cases) </li>
          <li>Menstrual calendar tracking</li>
        </ul>
      </section>

      <section>
        <h2> How We Treat It: Individualised, Gentle, Root-Cause Based</h2>
        <p>There is no one-size-fits-all treatment. Management depends on your age, whether you're 
trying to conceive, the underlying cause, and your lifestyle. </p>
        <h4>Lifestyle & Non-Pharmacologic Measures: </h4>
        <ul>
          <li>Diet changes (low-carb for PCOS, iodine-rich for thyroid)</li>
          <li>Exercise & stress reduction (yoga, meditation) </li>
          <li>Weight management </li>
        </ul>
        <h4>Medical Management</h4>
        <ul>
          <li>Hormonal therapy (OCPs, progestins)</li>
          <li>Thyroid medications (if needed)</li>
          <li>Metformin (for insulin resistance) </li>
          <li>Iron supplements (for anemia) </li>
          <li>Fertility planning (ovulation induction when needed)</li>
        </ul>
        <h4>Surgical Options (Only When Necessary): </h4>
        <ul>
          <li>Fibroid removal (Myomectomy) </li>
          <li>Polyp removal (Hysteroscopic polypectomy)</li>
          <li>Cyst aspiration (for large PCOS cysts) </li>
        </ul>
      </section>

      <section>
        <h2>Sukam’s Approach: Gentle Gynaecology with Clinical Rigor </h2>
        <p>
          We understand that menstrual health is both physical and emotional. Many young women 
suffer silently, thinking their problem is “normal” or “just hormonal”.
        </p>
        <p>
          At Sukam:
        </p>
        <ul>
          <li>✅ We offer private consultations, not rushed opinions</li>
          <li>✅ Our diagnosis is layered, not guesswork </li>
          <li>✅ Treatment is customised, not generic </li>
          <li>✅ And most importantly, we believe you — your pain, your confusion, your concern </li>
        </ul>
      </section>

      <section>
        <h2>👩‍💼  Real Stories, Real Relief</h2>
        <p>
          A 24-year-old engineering student came to us with only 3 periods in the past year. She 
had gained 7 kg, had facial hair and acne. Investigations confirmed PCOS. 
        </p>
        <p>
          With 4 months of dietary changes, hormonal balancing, and regular yoga, she now has 
regular periods — and her confidence is back. 
        </p>
        <p>
          With 4 months of dietary changes, hormonal balancing, and regular yoga, she now has 
regular periods — and her confidence is back. 
        </p>
        <p>A 35-year-old homemaker with two children presented with prolonged bleeding and 
clots. Ultrasound revealed multiple fibroids. After a planned myomectomy, she was 
relieved of heavy flow and weakness. </p>
      </section>

      <section>
        <h2>❤️ Final Word: Don't Suffer in Silence </h2>
        <p>
          Irregular periods are not “normal” or something to just “wait out.” They could be your body’s early cry for help.
        </p>
        <h2>Come talk to us at Sukam. </h2>
        <div className="cta-box">
          💡 We’ll listen, investigate, and walk with you until your cycle — and your confidence — returns
        </div>
      </section>
    </div>
  );
};

export default MenstrualHealthBlog;
